import { Link } from 'react-router-dom';
import SocialIcons from '../common/SocialIcons';
import ExternalLink from '../common/ExternalLink';
import SendPulseForm from '../common/SendPulseForm';

const Footer = () => {
  const startYear = 2024;
  const currentYear = new Date().getFullYear();
  const yearDisplay = currentYear > startYear 
    ? `${startYear}-${currentYear}`
    : startYear.toString();

  return (
    <footer className="bg-primary text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
          {/* Logo and Social Media */}
          <div className="md:col-span-1 text-center md:text-left">
            <img 
              src="https://rentfi.b-cdn.net/rentfi-logo-_1_.webp" 
              alt="RentFi Logo - Revolutionizing Real Estate Investment" 
              className="w-[110px] md:w-[146px] h-auto mb-4 mx-auto md:mx-0"
              loading="lazy"
              width="2024"
              height="1110"
            />
            <div className="flex justify-center md:justify-start">
              <SocialIcons />
            </div>
          </div>

          {/* Company Info */}
          <div className="text-center md:text-left">
            <h5 className="font-kanit mb-4">Company Info</h5>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/about" 
                  className="hover:text-secondary"
                  aria-label="Learn about RentFi's mission and vision"
                >
                  About
                </Link>
              </li>
              <li>
                <Link 
                  to="/contact" 
                  className="hover:text-secondary"
                  aria-label="Contact RentFi's support team"
                >
                  Contact
                </Link>
              </li>
              <li>
                <Link 
                  to="/roadmap" 
                  className="hover:text-secondary"
                  aria-label="View RentFi's development roadmap and milestones"
                >
                  Roadmap
                </Link>
              </li>
              <li>
                <Link 
                  to="/press" 
                  className="hover:text-secondary"
                  aria-label="Read RentFi's latest press releases and news"
                >
                  Press Releases
                </Link>
              </li>
            </ul>
          </div>

          {/* Resources */}
          <div className="text-center md:text-left">
            <h5 className="font-kanit mb-4">Resources</h5>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/faq" 
                  className="hover:text-secondary"
                  aria-label="Find answers to frequently asked questions about RentFi"
                >
                  FAQ
                </Link>
              </li>
              <li>
                <ExternalLink 
                  href="https://rentfi.substack.com/welcome" 
                  className="hover:text-secondary"
                  aria-label="Read RentFi's blog posts and updates on Substack"
                >
                  Blog
                </ExternalLink>
              </li>
              <li>
                <ExternalLink 
                  href="https://merch.rentfi.io" 
                  className="hover:text-secondary"
                  aria-label="Shop RentFi's official merchandise"
                >
                  Merch
                </ExternalLink>
              </li>
              <li>
                <Link 
                  to="/media-kit" 
                  className="hover:text-secondary"
                  aria-label="Access RentFi's brand assets and media resources"
                >
                  Media Kit
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div className="text-center md:text-left">
            <h5 className="font-kanit mb-4">Legal</h5>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/terms" 
                  className="hover:text-secondary"
                  aria-label="Read RentFi's terms of service"
                >
                  Terms
                </Link>
              </li>
              <li>
                <Link 
                  to="/privacy" 
                  className="hover:text-secondary"
                  aria-label="View RentFi's privacy policy"
                >
                  Privacy
                </Link>
              </li>
              <li>
                <Link 
                  to="/disclaimers" 
                  className="hover:text-secondary"
                  aria-label="Read important disclaimers about RentFi's services"
                >
                  Disclaimers
                </Link>
              </li>
              <li>
                <Link 
                  to="/cookie-policy" 
                  className="hover:text-secondary"
                  aria-label="Learn about RentFi's cookie policy and settings"
                >
                  Cookie Policy
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Blog Subscription - Hidden on mobile */}
        <div className="hidden md:block border-t border-emerald-700 pt-8 mb-8">
          <div className="text-center">
            <h5 className="font-kanit mb-4">Stay Updated</h5>
            <div className="flex justify-center">
              <div className="w-full max-w-[480px]">
                <SendPulseForm />
              </div>
            </div>
          </div>
        </div>

        {/* TrustPilot Link */}
        <div className="border-t border-emerald-700 pt-8 mb-8">
          <div className="text-center">
            <h5 className="font-kanit mb-4">Share Your Experience</h5>
            <div className="flex justify-center">
              <a 
                href="https://www.trustpilot.com/review/rentfi.io" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="hover:opacity-80 transition-opacity"
                aria-label="Review RentFi on Trustpilot"
              >
                <img 
                  src="https://rentfi.b-cdn.net/rentfi-trustpilot.webp"
                  alt="Trustpilot Review Platform Logo"
                  className="w-[240px] h-auto"
                  loading="lazy"
                  width="240"
                  height="120"
                />
              </a>
            </div>
          </div>
        </div>

        <div className="text-center">
          <p className="text-sm text-emerald-100">
            © {yearDisplay} RentFi. Part of{' '}
            <a 
              href="https://ownfi.io/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="hover:text-secondary"
              aria-label="Visit OwnFi's website, RentFi's parent company"
            >
              OwnFi
            </a>
            . All rights reserved.
          </p>
          
          <p className="text-sm text-emerald-100 mt-6">
            Investing in cryptocurrencies involves significant risk, including potential loss of principal. Past performance is not indicative of future results. See our Whitepaper for details.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;